import { apiService } from './api'
import i18n from '@/i18n'

// User types
export interface User {
  id: number
  email: string
  nickname: string
  name: string
  phone_number: string
  account_number?: string
  bank_name?: string
  wallet_address?: string
  created_at: string
  last_login_at?: string
  is_email_verified?: boolean
  is_active?: boolean

  // 회원 승인 관련 필드
  approval_status?: 'PENDING' | 'APPROVED' | 'REJECTED'
  approved_at?: string
  rejection_reason?: string

  // 멤버십 등급 정보
  membership_tier?: {
    id: number
    tier_level: number
    tier_name: string
    discount_rate: number
    required_volume: number
    is_active: boolean
    is_manual?: boolean
  }
  trading_volume_30d?: number

  // 추천인(파트너) 정보
  referral_code?: string
  referral_partner?: {
    id: number
    name: string
    username: string
    referral_code: string
  }

  // 추천인(커미션 회원) 정보
  commission_referrer?: {
    id: number
    nickname: string
    email: string
    commission_code: string
  }

  // 누적 금액 정보
  total_deposit?: number
  total_withdrawal?: number

  // IP 및 기기 정보
  registration_ip?: string
  last_login_ip?: string
  device_type?: string
  browser?: string
  same_ip_users?: Array<{
    id: number
    nickname: string
    registration_ip?: string
    last_login_ip?: string
    device_type?: string
    browser?: string
  }>
  same_ip_count?: number
  all_ips?: string[]
  has_ip_duplicate?: boolean
  duplicate_ip_count?: number
  has_device_duplicate?: boolean   // 같은 기기 다계정 여부 (2026-08-05 기기지문)
  duplicate_device_count?: number
  admin_memo?: string
  is_test_account?: boolean
  restrictions?: string[]  // 회원별 기능 제한 (DEPOSIT/WITHDRAWAL/TRADING/STAKING)
  // 모의투자(데모) (2026-08-08) — demo 필터 조회 시에만 비번/소유자 채워짐
  is_demo_account?: boolean
  demo_owner_user_id?: number | null
  demo_owner_nickname?: string | null
  demo_owner_email?: string | null
  demo_plain_password?: string | null
  // 상세 API 전용
  has_demo_account?: boolean | null
  demo_account_id?: number | null
  demo_owner_info?: { id: number; nickname?: string; email?: string } | null
}

export interface PartnerForFilter {
  id: number
  name: string
  username: string
  referral_code: string
}

export interface UserListResponse {
  users: User[]
  total: number
  page: number
  limit: number
}

export interface CreateUserRequest {
  email: string
  password: string
  nickname: string
  name: string
  phone_number: string
  account_number?: string
  bank_name?: string
  wallet_address?: string
}

export interface UpdateUserRequest {
  email?: string
  nickname?: string
  name?: string
  phone_number?: string
  account_number?: string
  bank_name?: string
  wallet_address?: string
  is_active?: boolean
}

export interface UserAsset {
  currency: string
  balance: number
  updated_at: string
}

export interface UserAssetsResponse {
  user: {
    id: number
    name: string
    email: string
    nickname: string
  }
  assets: {
    trading: Record<string, UserAsset>
    funding: Record<string, UserAsset>
  }
}

export interface UserDeposit {
  id: number
  deposit_type: string
  amount_krw: number
  amount_usd?: number
  exchange_rate?: number
  sender_name: string
  sender_address?: string
  crypto_currency?: string
  status: string
  rejection_reason?: string
  created_at: string
  approved_at?: string
  admin_bank_account?: {
    bank_name: string
    account_number: string
    account_holder: string
  }
  approved_by_admin?: {
    username: string
    name: string
  }
}

export interface UserDepositsResponse {
  user: {
    id: number
    name: string
    email: string
    nickname: string
  }
  deposits: UserDeposit[]
  pagination: {
    total: number
    page: number
    limit: number
    total_pages: number
  }
}

export interface TradingPosition {
  id: number
  symbol: string
  side: 'LONG' | 'SHORT'
  size: number
  entry_price: number
  mark_price?: number
  unrealized_pnl: number
  margin: number
  leverage: number
  liquidation_price?: number
  created_at: string
  status: string
}

export interface TradingHistory {
  id: number
  symbol: string
  side: 'LONG' | 'SHORT'
  action: string
  size: number
  price: number
  exit_price?: number | null
  realized_pnl: number
  fee: number
  created_at: string
  trading_balance_after?: number | null
  bonus_balance_after?: number | null
}

export interface UserPnlAnalysis {
  period_days: number
  total_profit: number
  total_loss: number
  net_pnl: number
  daily_pnl: Array<{
    date: string
    trade_count: number
    realized_pnl: number
    total_fees: number
    net_pnl: number
    profit: number
    loss: number
  }>
}

export interface UserStaking {
  id: number
  product_name: string
  amount: number
  staked_amount: number
  apy: number
  started_at?: string
  created_at?: string
  end_date?: string
  duration_days: number
  earned_rewards: number
  status: string
}

export interface BalanceChangeLog {
  id: number
  change_type: string
  balance_type: string
  amount: number
  trading_balance_before: number | null
  bonus_balance_before: number | null
  trading_balance_after: number | null
  bonus_balance_after: number | null
  description: string | null
  admin_name: string | null
  created_at: string
}

export interface UserInquiry {
  id: number
  subject: string
  title: string
  message: string
  content: string
  status: string
  created_at: string
  replied_at?: string
  admin_reply?: string
}

export interface UserChatMessage {
  id: number
  room_id: number
  is_admin: boolean
  sender_type: 'admin' | 'user'
  sender_id: number
  message: string
  content: string
  created_at: string
  is_read: boolean
}

class UsersService {
  // Get users list with pagination and search
  async getUsers(
    page: number = 1,
    limit: number = 20,
    search?: string,
    approval_status?: string,
    partner_id?: number,
    extra?: {
      test_account?: string
      status?: string
      membership_tier_id?: number
      has_login?: string
      email_verified?: string
      has_bank_account?: string
      has_wallet?: string
      registration_period?: string
      demo_account?: string   // 'demo'=데모만 / 'exclude'=제외(백엔드 기본) (2026-08-08)
    }
  ): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (search) {
      params.append('search', search)
    }

    if (approval_status) {
      params.append('approval_status', approval_status)
    }

    if (partner_id) {
      params.append('partner_id', partner_id.toString())
    }

    // 서버측 필터 (전체 total/페이징에 반영됨)
    if (extra) {
      const add = (k: string, v: any) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') params.append(k, String(v)) }
      add('test_account', extra.test_account)
      add('status', extra.status)
      add('membership_tier_id', extra.membership_tier_id)
      add('has_login', extra.has_login)
      add('email_verified', extra.email_verified)
      add('has_bank_account', extra.has_bank_account)
      add('has_wallet', extra.has_wallet)
      add('registration_period', extra.registration_period)
      add('demo_account', extra.demo_account)
    }

    const response = await apiService.get<any>(`/api/v1/admin/users?${params}`)
    return {
      users: response.data?.users || [],
      total: (response as any)?.total ?? 0,
      page: (response as any)?.page ?? page,
      limit: (response as any)?.limit ?? limit,
    }
  }

  // Get partners list for filter
  async getPartnersForFilter(): Promise<PartnerForFilter[]> {
    const response = await apiService.get<any>('/api/v1/admin/users/partners/list')
    return response.data?.partners || []
  }

  // 회원 정보 전체 엑셀(XLSX) 추출 (인증 blob) — 상세 필터 적용 + 관리자 메모 열 포함
  async exportMembersCsv(filters: Record<string, any> = {}): Promise<void> {
    const p = new URLSearchParams()
    const add = (k: string, v: any) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') p.append(k, String(v)) }
    add('search', filters.search)
    add('approval_status', filters.approval_status)
    add('partner_id', filters.partner_id)
    add('test_account', filters.test_account)
    add('status', filters.status)
    add('membership_tier_id', filters.membership_tier_id)
    add('has_login', filters.has_login)
    add('email_verified', filters.email_verified)
    add('has_bank_account', filters.has_bank_account)
    add('has_wallet', filters.has_wallet)
    add('has_memo', filters.has_memo)
    add('registration_period', filters.registration_period)
    add('start_date', filters.start_date)
    add('end_date', filters.end_date)
    const resp = await apiService.getClient().get(`/api/v1/admin/users/export?${p}`, { responseType: 'blob' })
    const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const cd: string = resp.headers?.['content-disposition'] || ''
    const m = cd.match(/filename=([^;]+)/)
    a.download = (m ? m[1].trim() : `members_${Date.now()}.xlsx`)
    document.body.appendChild(a); a.click(); a.remove()
    window.URL.revokeObjectURL(url)
  }

  // Get single user details
  async getUserById(userId: number): Promise<User> {
    console.log('getUserById 호출:', userId)
    
    try {
      // Raw axios call for debugging
      const rawResponse = await apiService.getClient().get(`/api/v1/admin/users/${userId}`)
      console.log('Raw 응답:', rawResponse.data)
      
      const response = await apiService.get(`/api/v1/admin/users/${userId}`)
      console.log('Wrapped 응답:', response)
      
      // 백엔드가 UserResponse를 직접 반환하는 경우 (response_model=UserResponse)
      // apiService.get은 ApiResponse<T>를 반환하지만, 백엔드가 직접 객체를 반환하면
      // response 자체가 User 객체일 수 있음
      if (response && typeof response === 'object') {
        // response가 User 객체인 경우 (id 필드가 있으면 User 객체)
        if ('id' in response && 'email' in response) {
          console.log('✅ User 객체 직접 반환:', response)
          return response as unknown as User
        }
        // response가 ApiResponse 형식인 경우 (result, data 필드가 있음)
        if ('result' in response && 'data' in response && response.data) {
          console.log('✅ ApiResponse 형식:', response.data)
          return response.data as User
        }
      }
      
      // 기타 경우
      console.warn('⚠️ 예상치 못한 응답 형식:', response)
      return response as unknown as User
    } catch (error: any) {
      console.error('getUserById 에러:', error)
      console.error('에러 응답:', error?.response?.data)
      throw error
    }
  }

  // 상담 메모 조회 (현재값 + 저장 이력) — 회원 메모(admin_memo)와 별개 (2026-08-09)
  async getConsultation(userId: number): Promise<{ memo: string; logs: { id: number; content: string; admin_name: string; created_at: string }[] }> {
    const r = await apiService.getClient().get(`/api/v1/admin/users/${userId}/consultation`)
    const d = (r.data?.data ?? r.data) as any
    return { memo: d?.memo || '', logs: d?.logs || [] }
  }
  // 상담 메모 저장 (+이력 자동 기록)
  async saveConsultation(userId: number, memo: string): Promise<void> {
    await apiService.getClient().put(`/api/v1/admin/users/${userId}/consultation`, { memo })
  }

  // Create new user
  async createUser(userData: CreateUserRequest): Promise<void> {
    try {
      console.log('Creating user with data:', userData)
      const response = await apiService.post('/api/v1/admin/users', userData)
      console.log('Create user response:', response)
    } catch (error: any) {
      console.error('Create user error:', error)
      console.error('Error response:', error?.response?.data)
      throw error
    }
  }

  // Get bank list
  async getBanks(): Promise<Array<{ code: string; name: string }>> {
    const resp = await apiService.get<any>('/api/v1/auth/banks')
    // resp.data is array of categories with banks list
    const categories = resp.data || []
    const banks: Array<{ code: string; name: string }> = []
    categories.forEach((cat: any) => {
      ;(cat.banks || []).forEach((b: any) => {
        if (b?.code && b?.name) banks.push({ code: b.code, name: b.name })
      })
    })
    return banks
  }

  // Update user
  async updateUser(userId: number, userData: UpdateUserRequest): Promise<void> {
    await apiService.put(`/api/v1/admin/users/${userId}`, userData)
  }

  // Toggle test-account flag
  async setTestFlag(userId: number, isTest: boolean): Promise<void> {
    await apiService.patch(`/api/v1/admin/users/${userId}/test-flag`, { is_test: isTest })
  }

  async updateUserMemo(userId: number, adminMemo: string): Promise<void> {
    await apiService.patch(`/api/v1/admin/users/${userId}/memo`, {
      admin_memo: adminMemo
    })
  }

  // Delete user
  async deleteUser(userId: number): Promise<void> {
    await apiService.delete(`/api/v1/admin/users/${userId}`)
  }

  // Get user assets
  async getUserAssets(userId: number): Promise<UserAssetsResponse> {
    const response = await apiService.get<UserAssetsResponse>(`/api/v1/admin/users/${userId}/assets`)
    return response.data!
  }

  // Get user deposits
  async getUserDeposits(
    userId: number, 
    page: number = 1, 
    limit: number = 10
  ): Promise<UserDepositsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    const response = await apiService.get<UserDepositsResponse>(`/api/v1/admin/users/${userId}/deposits?${params}`)
    return response.data!
  }

  // Export phone numbers with filters
  async exportPhoneNumbers(filters: {
    approvalStatus?: string
    status?: string
    membershipTierId?: number
    partnerId?: number
    missionType?: string
    missionStep?: number
    missionStatus?: string
    startDate?: string
    endDate?: string
  }): Promise<{
    total: number
    phones: Array<{ id: number; phone_number: string; name: string; nickname: string }>
  }> {
    const params = new URLSearchParams()
    if (filters.approvalStatus && filters.approvalStatus !== 'all') params.append('approval_status', filters.approvalStatus)
    if (filters.status && filters.status !== 'all') params.append('status', filters.status)
    if (filters.membershipTierId) params.append('membership_tier_id', filters.membershipTierId.toString())
    if (filters.partnerId) params.append('partner_id', filters.partnerId.toString())
    if (filters.missionType && filters.missionType !== 'all') params.append('mission_type', filters.missionType)
    if (filters.missionStep) params.append('mission_step', filters.missionStep.toString())
    if (filters.missionStatus && filters.missionStatus !== 'all') params.append('mission_status', filters.missionStatus)
    if (filters.startDate) params.append('start_date', filters.startDate)
    if (filters.endDate) params.append('end_date', filters.endDate)

    const response = await apiService.get(`/api/v1/admin/users/phone-export?${params}`)
    return response.data
  }

  // Get user trading history
  async getUserTradingHistory(
    userId: number,
    page: number = 1,
    limit: number = 20,
    symbol?: string,
    action?: string,
    startDate?: string,
    endDate?: string,
    actionType?: string
  ): Promise<{
    positions: TradingHistory[]
    total_count: number
    page: number
    pages: number
    summary?: {
      total_count: number
      total_profit: number
      total_loss: number
      net_pnl: number
      total_fee: number
    }
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (symbol) params.append('symbol', symbol)
    if (action) params.append('action', action)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (actionType) params.append('action_type', actionType)

    const response = await apiService.get(`/api/v1/admin/users/${userId}/trading-history?${params}`)
    return response.data
  }

  // Get user trading volume history (VIP calculation)
  async getUserTradingVolumeHistory(
    userId: number,
    page: number = 1,
    limit: number = 20,
    symbol?: string,
    startDate?: string,
    endDate?: string,
    vipCounted?: boolean
  ): Promise<{
    volumes: Array<{
      id: number
      position_id: number | null
      symbol: string
      action_type: string
      side: string
      quantity: number
      price: number
      leverage: number
      trading_volume: number
      vip_volume: number
      is_vip_counted: boolean
      fee: number
      realized_pnl: number
      created_at: string
      filled_at: string | null
    }>
    summary: {
      total_volume: number
      total_vip_volume: number
      total_count: number
    }
    total: number
    page: number
    pages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    if (symbol) params.append('symbol', symbol)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (vipCounted !== undefined) params.append('vip_counted', vipCounted.toString())

    const response = await apiService.get(`/api/v1/admin/users/${userId}/trading-volume-history?${params}`)
    return response.data
  }

  // Get user positions
  async getUserPositions(userId: number): Promise<{
    positions: TradingPosition[]
    summary: {
      total_positions: number
      long_positions: number
      short_positions: number
      total_unrealized_pnl: number
    }
  }> {
    const response = await apiService.get(`/api/v1/admin/users/${userId}/positions`)
    return response.data
  }

  // Get user PnL analysis
  async getUserPnlAnalysis(userId: number, days: number = 30): Promise<UserPnlAnalysis> {
    const response = await apiService.get<UserPnlAnalysis>(`/api/v1/admin/users/${userId}/pnl-analysis?days=${days}`)
    return response.data!
  }

  // Give bonus to user
  async giveUserBonus(
    userId: number, 
    amount: number, 
    reason: string = 'MANUAL', 
    memo: string = ''
  ): Promise<void> {
    await apiService.post(`/api/v1/admin/users/${userId}/bonus`, {
      amount,
      reason,
      memo
    })
  }

  // Get user staking
  async getUserStaking(userId: number): Promise<{
    total_staked: number
    total_rewards: number
    active_stakings: number
    stakings: UserStaking[]
  }> {
    const response = await apiService.get(`/api/v1/admin/users/${userId}/staking`)
    return response.data
  }

  // Get user balance change logs
  async getUserBalanceLogs(
    userId: number,
    page: number = 1,
    limit: number = 20,
    changeType?: string
  ): Promise<{
    items: BalanceChangeLog[]
    total: number
    page: number
    limit: number
  }> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() })
    if (changeType) params.append('change_type', changeType)
    const response = await apiService.get(`/api/v1/admin/users/${userId}/balance-logs?${params}`)
    return response.data
  }

  // Get user inquiries
  async getUserInquiries(
    userId: number,
    page: number = 1,
    limit: number = 20,
    status_filter?: string
  ): Promise<{
    inquiries: UserInquiry[]
    total: number
    page: number
    pages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    if (status_filter) params.append('status_filter', status_filter)

    const response = await apiService.get(`/api/v1/admin/users/${userId}/inquiries?${params}`)
    return response.data?.data || response.data
  }

  // Get user chat rooms
  async getUserChatHistory(
    userId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    rooms: Array<{
      room_id: number
      status: string
      title: string | null
      message_count: number
      last_message: {
        content: string
        sender_type: string
        message_type: string
        created_at: string
      } | null
      created_at: string
      updated_at: string
    }>
    total: number
    page: number
    pages: number
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    const response = await apiService.get(`/api/v1/admin/users/${userId}/chat-history?${params}`)
    return response.data?.data || response.data
  }

  // Get user summary
  async getUserSummary(userId: number): Promise<{
    user_info: {
      id: number
      name: string
      email: string
      nickname: string
      created_at: string
      last_login_at?: string
      is_email_verified: boolean
    }
    financial_summary: {
      total_deposits: number
      total_withdrawals: number
      active_positions: number
    }
  }> {
    console.log('getUserSummary 호출:', userId)
    const response = await apiService.get(`/api/v1/admin/users/${userId}/summary`)
    console.log('getUserSummary 응답:', response)
    return response.data
  }

  // 보너스 지급
  async grantBonus(userId: number, amount: number, memo?: string): Promise<void> {
    await apiService.post(`/api/v1/admin/bonus/grant`, {
      user_id: userId,
      amount_usdt: amount,
      memo: memo || i18n.t('users:detail.adminBonusGrant')
    })
  }

  // 비밀번호 재설정
  async resetPassword(userId: number, newPassword: string): Promise<void> {
    await apiService.post(`/api/v1/admin/users/${userId}/reset-password`, {
      new_password: newPassword
    })
  }

  // 회원 승인
  async approveUser(userId: number): Promise<void> {
    await apiService.post(`/api/v1/admin/users/${userId}/approve`)
  }

  // 회원 거부
  async rejectUser(userId: number, rejectionReason: string): Promise<void> {
    await apiService.post(`/api/v1/admin/users/${userId}/reject`, {
      rejection_reason: rejectionReason
    })
  }

  // 멤버십 등급 목록 조회
  async getMembershipTiers(): Promise<{
    tiers: Array<{
      id: number
      tier_level: number
      tier_name: string
      discount_rate: number
      required_volume: number
    }>
  }> {
    const response = await apiService.get('/api/v1/admin/users/membership-tiers/list')
    return response.data
  }

  // 회원 등급 강제 변경
  async updateUserMembershipTier(userId: number, tierId: number): Promise<void> {
    await apiService.post(`/api/v1/admin/users/${userId}/membership-tier?tier_id=${tierId}`)
  }

  // 회원 등급 자동(거래량 기준)으로 전환 — 수동 고정 해제
  async resetMembershipTierToAuto(userId: number) {
    return apiService.post(`/api/v1/admin/users/${userId}/membership-tier/auto`)
  }

  // ==================== 회원 커미션 시스템 API ====================

  // 커미션 코드 발급
  async issueCommissionCode(userId: number, code?: string): Promise<{ commission_code: string }> {
    try {
      const response = await apiService.post<{ commission_code: string }>(`/api/v1/admin/users/${userId}/commission-code`, { code })
      console.log('🔍 issueCommissionCode response:', response)
      // apiService.post는 ApiResponse<T>를 반환하므로 response.data를 반환
      if (response && response.result === 'success' && response.data) {
        console.log('✅ Commission code issued:', response.data.commission_code)
    return response.data
      }
      throw new Error(response?.message || i18n.t('users:detail.commissionCodeFailed'))
    } catch (error: any) {
      console.error('❌ issueCommissionCode error:', error)
      throw error
    }
  }

  // 커미션 설정 조회
  async getCommissionSettings(userId: number): Promise<{
    commission_code: string | null
    settings: {
      entry_commission_rate: number
      close_commission_rate: number
      payout_type: 'USDT' | 'BONUS'
      is_active: boolean
    } | null
    referrer_type: 'partner' | 'commission' | null
    referrer: {
      id: number
      name?: string
      username?: string
      nickname?: string
      email?: string
      referral_code?: string
      commission_code?: string
    } | null
  }> {
    try {
    const response = await apiService.get(`/api/v1/admin/users/${userId}/commission-settings`)
      console.log('🔍 getCommissionSettings response:', response)
      if (response && response.result === 'success' && response.data) {
    return response.data
      }
      throw new Error(response?.message || i18n.t('users:detail.commissionSettingsFailed'))
    } catch (error: any) {
      console.error('❌ getCommissionSettings error:', error)
      throw error
    }
  }

  // 커미션 설정 수정
  async updateCommissionSettings(
    userId: number,
    settings: {
      entry_commission_rate?: number
      close_commission_rate?: number
      payout_type?: 'USDT' | 'BONUS'
      is_active?: boolean
    }
  ): Promise<void> {
    await apiService.put(`/api/v1/admin/users/${userId}/commission-settings`, settings)
  }

  // 커미션 회원 리스트 (이 회원의 코드로 가입한 회원들)
  async getCommissionMembers(
    userId: number,
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<{
    total: number
    page: number
    pages: number
    members: Array<{
      id: number
      nickname: string
      email: string
      created_at: string
    }>
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (search) params.append('search', search)

    const response = await apiService.get(`/api/v1/admin/users/${userId}/commission-members?${params}`)
    return response.data
  }

  // 커미션 로그 조회
  async getCommissionLogs(
    userId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    total: number
    page: number
    pages: number
    logs: Array<{
      id: number
      trader_id: number
      trader_nickname: string
      symbol: string
      quantity: string
      price: string
      fee_type: 'ENTRY' | 'CLOSE'
      original_fee: string
      commission_rate: string
      commission_amount: string
      payout_type: 'USDT' | 'BONUS'
      status: 'PENDING' | 'PAID' | 'REVERSED'
      created_at: string
    }>
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    const response = await apiService.get(`/api/v1/admin/users/${userId}/commission-logs?${params}`)
    return response.data
  }

  // 추천인 변경
  async updateUserReferral(
    userId: number,
    type: 'partner' | 'commission' | 'none',
    referrerId?: number,
    code?: string
  ): Promise<void> {
    await apiService.put(`/api/v1/admin/users/${userId}/referral`, {
      type,
      referrer_id: referrerId,
      code
    })
  }

  // 파트너 검색 (추천인 변경용)
  async searchPartners(query: string): Promise<{
    partners: Array<{
      id: number
      name: string
      username: string
      referral_code: string
    }>
  }> {
    const response = await apiService.get(`/api/v1/admin/users/partners/search?q=${encodeURIComponent(query)}`)
    return response.data
  }

  // 커미션 회원 검색 (추천인 변경용)
  async searchCommissionCodes(query: string): Promise<{
    users: Array<{
      id: number
      nickname: string
      email: string
      commission_code: string
    }>
  }> {
    const response = await apiService.get(`/api/v1/admin/users/commission-codes/search?q=${encodeURIComponent(query)}`)
    return response.data
  }
}

export const usersService = new UsersService()
export default usersService